import * as React from "react";
import { useEffect } from "react";
import { Button, Gapped, Modal, Toast } from "react-ui";
import { useDispatch } from "react-redux";
import { Link } from "@skbkontur/react-ui";

import { hideModalActionCreator } from "../../FormModalAction";
import {
    CombinatorType,
    CombineFufResult,
    FufCombineMethod,
} from "../../../../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";
import { CustomFile } from "../../../../../Helpers/file";
import { ModalStateInfo } from "../../IModalState";
import { ParsedUploadFileProperties } from "../../../../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";
import { isNotNullOrEmpty } from "../../../../../../../Common/TypingUtils";
import { useMetrics } from "../../../../../Controls/Internals/Metrics/MetricsProviderContext";
import {
    PATCH_FUF_MODAL_CLOSE,
    PATCH_FUF_MODAL_OPEN,
    XML_UPLOAD_FILE,
    XML_UPLOAD_SELECT_ADD,
    XML_UPLOAD_SELECT_REPLACE,
} from "../../../../../Controls/metricsAction";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { extractFileExtension, FileProps, UploadFileStatuses, PatchFufSteps } from "../Utils/Types";
import { ModalText, StepPage } from "../Utils/Styles/Modals.styles";
import { useUploadFile } from "../Utils/UploadFileRedisign/UseUploadFile";
import { getAcceptFilesError, getStepHeaderContent } from "../Utils/TextContent";
import { wideModalWidth } from "../Utils/Styles/ModalWidth";
import { CombineMethodPicker } from "../Utils/CombineMethodPicker/CombineMethodPicker";
import { useFormObject } from "../../../../../Controls/FormObjectProvider";
import { MergeResult } from "../Utils/MergeResult/MergeResult";
import { ModalLoader } from "../Utils/ModalLoader/ModalLoader";
import { FileException } from "../Utils/UploadFileRedisign/UploadFile.styles";

export interface PatchFufByAttachmentModalProps {
    modalSettings?: ModalStateInfo;
    currentGfv: string;
    importXmls: (
        driveUris: string[],
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: string[]
    ) => Promise<CombineFufResult | undefined>;
    updateFuf: (driveUris: string[], abortSignal: AbortSignal) => Promise<ParsedUploadFileProperties>;
    uploadFile: () => Promise<CustomFile | undefined>;
}

export interface ValidDriveURL {
    id: string;
    prevDocsInstancesCount: number;
    fufUri: string;
}

export const PatchFufByAttachmentModal = ({ modalSettings, importXmls, updateFuf }: PatchFufByAttachmentModalProps) => {
    const dispatch = useDispatch();
    const track = useMetrics();
    const form = useFormObject();

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<React.ReactNode>();
    const [step, setStep] = React.useState<PatchFufSteps>(PatchFufSteps.SelectCombineFufMethod);
    const [combineMethod, setCombineMethod] = React.useState<FufCombineMethod>(FufCombineMethod.AddToEnd);
    const abortController = React.useRef(new AbortController());

    const [mergeResult, setMergeResult] = React.useState<CombineFufResult | undefined>(undefined);

    const { filesContainer, uploadedFiles, validDriveURLs, addUserFilesToDrive } = useUploadFile<ValidDriveURL>({
        filterValidDriveUrl: filterValidFiles,
        checkValidFile: checkValidFile,
    });

    function filterValidFiles(fileToRemove: FileProps, file: ValidDriveURL) {
        return file.id !== fileToRemove.id;
    }

    React.useEffect(() => {
        const countFromFufs = validDriveURLs.reduce((acc, file) => (acc += +file.prevDocsInstancesCount), 0);
        const totalCount = countFromFufs + (modalSettings?.currentInstancesCount ?? 0);
        const maxInstances = modalSettings?.maxInstances ?? 0;
        if (combineMethod === FufCombineMethod.ReplaceThenMerge && countFromFufs > maxInstances) {
            setOverDraftError();
        } else if (combineMethod === FufCombineMethod.Merge && totalCount > maxInstances) {
            setOverDraftError();
        } else {
            setError(null);
        }
    }, [validDriveURLs, combineMethod]);

    useAsyncEffect(async () => {
        if (modalSettings?.currentInstancesCount === 0) {
            setStep(PatchFufSteps.SelectFiles);
            setCombineMethod(FufCombineMethod.Merge);
            await addUserFilesToDrive();
        } else {
            setStep(PatchFufSteps.SelectCombineFufMethod);
        }
    }, []);

    useEffect(() => {
        track(PATCH_FUF_MODAL_OPEN, {
            modalName: modalSettings?.modalName,
            acceptFiles: modalSettings?.acceptFiles.toString(),
        });
    }, []);

    const onClose = async () => {
        abortController.current.abort();
        track(PATCH_FUF_MODAL_CLOSE, {
            modalName: modalSettings?.modalName,
            acceptFiles: modalSettings?.acceptFiles.toString(),
        });
        dispatch(hideModalActionCreator());
    };

    const onCancelLoading = async () => {
        abortController.current.abort();
        setLoading(false);
    };

    async function checkValidFile(
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<ValidDriveURL[]>>
    ): Promise<FileProps> {
        let errorMessageText: string | JSX.Element = "";
        let instancesCount;
        if (!modalSettings?.acceptFiles?.includes(extractFileExtension(file.uploadFile.name))) {
            errorMessageText = getAcceptFilesError(modalSettings?.acceptFiles);
        }
        const driveUriGuard = file.driveUri;

        if (file.id && driveUriGuard && !errorMessageText) {
            const properties = await updateFuf([driveUriGuard], abortController.current.signal);
            if (properties.fufUri && modalSettings?.acceptGFVs?.includes(properties.gfv)) {
                const newValidFile: ValidDriveURL = {
                    id: file.id,
                    prevDocsInstancesCount: properties.prevDocsInstancesCount,
                    fufUri: properties.fufUri,
                };
                instancesCount = properties.prevDocsInstancesCount;
                setValidDriveURLs(value => [...value, newValidFile]);
            } else {
                const fileContentHelpLink = (
                    <Link target="_blank" href="https://www.kontur-extern.ru/support/faq/41/843">
                        Что должно быть в файле.
                    </Link>
                );
                errorMessageText = <>Неподходящее содержимое файла. {fileContentHelpLink}</>;
            }
        }
        const status = errorMessageText ? UploadFileStatuses.error : UploadFileStatuses.success;
        track(XML_UPLOAD_FILE, {
            path: modalSettings?.pathToTable,
            uploadFile: file.uploadFile.name,
            size: file.uploadFile.size,
            status: status,
        });
        return {
            id: file.id,
            uploadFile: file.uploadFile,
            status: status,
            errorMessage: <FileException>{errorMessageText}</FileException>,
            driveUri: file.driveUri,
            instancesInfo: {
                count: instancesCount,
                description: modalSettings?.instanceCountFromProps?.description,
            },
        };
    }

    const onDownloadFileHandler = async () => {
        await uploadFiles();
    };

    const setOverDraftError = () => {
        const loadingErrorMessage = (
            <ModalText>
                Получится слишком большое уведомление. <br /> — Более 1 000 записей о ранее представленных документах
                <br /> — Или по документам указано более 1 000 записей с именами файлов <br />
                <br /> Разделите данные и отправьте их отдельными уведомлениями.
            </ModalText>
        );
        setError(loadingErrorMessage);
    };

    const getDriveUrls = () =>
        uploadedFiles
            .map(file => (file.status !== UploadFileStatuses.error ? file.driveUri : undefined))
            .filter(isNotNullOrEmpty);

    async function refreshUpload() {
        Toast.close();
        await onDownloadFileHandler();
    }

    const uploadFiles = async () => {
        setLoading(true);

        const response = await importXmls(
            validDriveURLs.map(file => file.fufUri),
            combineMethod,
            CombinatorType.Default,
            false,
            []
        );

        if (response !== undefined) {
            if (response.addedItemsCount !== 0 || response.updatedItemsCount !== 0) {
                await form.reloadInner();
                setMergeResult(response);
                setStep(PatchFufSteps.ShowCombineResult);
            } else {
                await onClose();
                Toast.push("В файле нет данных для загрузки");
            }
        } else {
            setStep(PatchFufSteps.SelectFiles);
            Toast.push("Не удалось загрузить файл", {
                label: "Повторить",
                handler: refreshUpload,
            });
        }
        setLoading(false);
    };

    const setCombineMethodAndUploadFiles = async (newCombineMethod: FufCombineMethod) => {
        setStep(PatchFufSteps.SelectFiles);
        setCombineMethod(newCombineMethod);
        await addUserFilesToDrive();
    };

    const clickOnReplaceButton = async () => {
        track(XML_UPLOAD_SELECT_REPLACE, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(FufCombineMethod.ReplaceThenMerge);
    };

    const clickOnAddButton = async () => {
        track(XML_UPLOAD_SELECT_ADD, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(FufCombineMethod.Merge);
    };

    const isSomeFileLoading = uploadedFiles.some(
        file => file.status === UploadFileStatuses.new || file.status === UploadFileStatuses.progress
    );

    const isButtonLoadDisabled = loading || !Boolean(getDriveUrls().length) || isSomeFileLoading || Boolean(error);

    const isHaveValidAndInvalidFile =
        uploadedFiles.some(item => item.status === UploadFileStatuses.error) &&
        uploadedFiles.some(item => item.status === UploadFileStatuses.success);

    return (
        <Modal width={wideModalWidth} ignoreBackgroundClick onClose={onClose}>
            <Modal.Header>{getStepHeaderContent(combineMethod)}</Modal.Header>
            <Modal.Body style={{ paddingBottom: 16 }}>
                <ModalLoader loading={loading}>
                    <>
                        <StepPage visible={step === PatchFufSteps.SelectCombineFufMethod}>
                            <CombineMethodPicker
                                onMergeClick={
                                    modalSettings?.combineType === FufCombineMethod.Merge ||
                                    modalSettings?.combineType === undefined
                                        ? clickOnAddButton
                                        : undefined
                                }
                                onAddToEndClick={
                                    modalSettings?.combineType === FufCombineMethod.AddToEnd
                                        ? clickOnAddButton
                                        : undefined
                                }
                                onReplaceClick={clickOnReplaceButton}
                            />
                        </StepPage>
                        <StepPage visible={step === PatchFufSteps.SelectFiles}>{filesContainer}</StepPage>
                        <StepPage visible={step === PatchFufSteps.ShowCombineResult}>
                            <MergeResult mergeResult={mergeResult} documentName={"документов"} />
                        </StepPage>
                        <FileException>{error}</FileException>
                    </>
                </ModalLoader>
            </Modal.Body>
            <Modal.Footer
                panel
                sticky
                style={{ display: step === PatchFufSteps.SelectCombineFufMethod ? "none" : "block" }}>
                <Gapped gap={16}>
                    {step !== PatchFufSteps.ShowCombineResult && (
                        <Button
                            disabled={isButtonLoadDisabled}
                            onClick={onDownloadFileHandler}
                            use="primary"
                            size="medium">
                            Загрузить файлы {isHaveValidAndInvalidFile && !isButtonLoadDisabled && " без ошибок"}
                        </Button>
                    )}
                    {loading ? (
                        <Button onClick={onCancelLoading} size="medium">
                            Отменить
                        </Button>
                    ) : (
                        <Button onClick={onClose} size="medium">
                            Закрыть
                        </Button>
                    )}
                </Gapped>
            </Modal.Footer>
        </Modal>
    );
};
